import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { queryClient } from './query-client';
import { serializeParams } from './api-params';
import { installCsrfRetry } from './csrf-retry';
import type { PaginatedResponse, PaginationMeta } from './types/api';

/**
 * Auth injection — the shared api client must not depend on app state. The app
 * wires its auth store via configureApiAuth() at startup (see lib/bootstrap.ts),
 * so this client works with either auth model (platform User / tenant Employee).
 */
type AuthAccessor = () => { token?: string | null; logout?: () => void };
let authAccessor: AuthAccessor = () => ({});
export function configureApiAuth(accessor: AuthAccessor): void {
  authAccessor = accessor;
}

/**
 * Laravel Sanctum SPA Authentication API Client
 * 
 * This client follows the official Laravel Sanctum SPA authentication pattern:
 * 1. Get CSRF cookie from /sanctum/csrf-cookie
 * 2. Login via /login (web route, NOT /api/platform/login)
 * 3. Laravel sets httpOnly session cookie
 * 4. All API requests include session cookie automatically
 * 5. CSRF token from cookie sent in X-XSRF-TOKEN header
 * 
 * NO TOKENS STORED - Session cookies handle authentication
 */

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    // Same-origin API in the browser (platform and tenant)
    const baseURL = typeof window !== 'undefined'
      ? ''
      : import.meta.env.VITE_API_URL || 'http://api.marine-dms.local';

    this.instance = axios.create({
      baseURL,
      withCredentials: true, // Required for Sanctum SPA (sends cookies)
      withXSRFToken: true,   // Axios automatically extracts and sends XSRF token
      paramsSerializer: { serialize: serializeParams },
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    installCsrfRetry(this.instance, {
      onSessionDead: () => {
        localStorage.removeItem('auth-storage');
        authAccessor().logout?.();
        queryClient.clear();
      },
    });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Error interceptor: global error handling (401/419/5xx)
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Network timeout or connection error
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          console.error('Network error:', error.message);
          // Don't redirect, just let the error propagate to show in UI
          return Promise.reject({
            ...error,
            message: 'Network error. Please check your connection.',
          });
        }

        // Unauthorized - clear auth and let React Router handle redirect
        if (error.response?.status === 401) {
          console.log('Unauthorized request, clearing auth state');
          
          // Clear auth state
          localStorage.removeItem('auth-storage');
          authAccessor().logout?.();
          
          // Clear React Query cache
          queryClient.clear();
          
          // Don't use window.location.href - let TenantProtectedRoute handle redirect
          // This prevents unnecessary page reloads and preserves React Router state
        }

        // Server error (5xx)
        if (error.response?.status >= 500) {
          console.error('Server error:', error.response?.data || error.message);
          return Promise.reject({
            ...error,
            message: error.response?.data?.error?.message || error.response?.data?.message || 'Server error. Please try again later.',
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Get CSRF cookie from Laravel
   * Must be called before login for Sanctum SPA auth
   */
  async getCsrfCookie(): Promise<void> {
    await this.instance.get('/sanctum/csrf-cookie');
  }

  /**
   * Login using Laravel Sanctum SPA pattern
   * Uses web route /login (NOT /api/platform/login)
   */
  async login(credentials: { email: string; password: string; remember?: boolean }) {
    // CSRF is seeded by the same-origin blade shell (W4 D5). No pre-call; the
    // shared 419 interceptor recovers on rotation/expiry.
    const { data } = await this.instance.post('/login', credentials);
    return data;
  }

  /**
   * Logout using web route
   */
  async logout() {
    await this.instance.post('/logout');
  }

  // HTTP methods for API calls
  // TODO: Replace any with typed request/response configs once API response types are defined.
  get<T = any>(url: string, config?: any) {
    return this.instance.get<T>(url, config);
  }

  post<T = any>(url: string, data?: any, config?: any) {
    return this.instance.post<T>(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: any) {
    return this.instance.put<T>(url, data, config);
  }

  patch<T = any>(url: string, data?: any, config?: any) {
    return this.instance.patch<T>(url, data, config);
  }

  delete<T = any>(url: string, config?: any) {
    return this.instance.delete<T>(url, config);
  }

  // ── Typed convenience methods (unwrap Laravel {data:…} envelope) ──

  /** Single resource: backend {data: T} -> returns T */
  async getOne<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<{ data: T }>(url, config);
    return response.data.data;
  }

  /** Non-paginated collection: backend {data: T[]} -> returns T[] */
  async getList<T>(url: string, config?: AxiosRequestConfig): Promise<T[]> {
    const response = await this.instance.get<{ data: T[] }>(url, config);
    return response.data.data;
  }

  /** Paginated collection: backend {data: T[], meta: {...}, ...extras} -> returns PaginatedResponse<T> */
  async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResponse<T>> {
    const response = await this.instance.get<{ data: T[]; meta: PaginationMeta; [key: string]: unknown }>(url, config);
    const { data, meta, ...extra } = response.data;
    return { items: data, meta: { ...meta, ...extra } as PaginationMeta };
  }

  /** Create/action returning data: backend {data: T} -> returns T */
  async postOne<T = void>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** For POST that returns {message: "..."} with no data */
  async postAction(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<string> {
    const response = await this.instance.post<{ message: string }>(url, data, config);
    return response.data.message;
  }

  /** Update: backend {data: T} -> returns T */
  async putOne<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** Patch: backend {data: T} -> returns T */
  async patchOne<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** Delete: backend {message: "..."} -> returns message */
  async deleteOne(url: string, config?: AxiosRequestConfig): Promise<string> {
    const response = await this.instance.delete<{ message: string }>(url, config);
    return response.data.message;
  }

  /**
   * Get raw axios instance if needed for advanced usage
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Export singleton instance
export const api = new ApiClient();
